import React from 'react';

/**
 * MaturityGauge - SVG circular gauge for displaying maturity scores (1.0-4.0)
 *
 * Props:
 * - score: number (1.0 - 4.0)
 * - size: 'small' | 'medium' | 'large' (default: 'medium')
 * - showLabel: boolean (default: true) - show maturity level label below score
 * - animate: boolean (default: true) - animate on mount
 */
const MaturityGauge = ({ score = 1.0, size = 'medium', showLabel = true, animate = true }) => {
  // Clamp score to valid range
  const clampedScore = Math.max(1.0, Math.min(4.0, score));

  // Calculate percentage (1.0 = 0%, 4.0 = 100%)
  const percentage = ((clampedScore - 1) / 3) * 100;

  // Size configurations
  const sizes = {
    small: { width: 80, strokeWidth: 6, fontSize: 16, labelSize: 10 },
    medium: { width: 120, strokeWidth: 8, fontSize: 24, labelSize: 12 },
    large: { width: 160, strokeWidth: 10, fontSize: 32, labelSize: 14 }
  };

  const config = sizes[size] || sizes.medium;
  const radius = (config.width - config.strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const arcLength = circumference * 0.75; // 270 degrees
  const dashOffset = arcLength - (arcLength * percentage / 100);

  // Get maturity level label and color based on score
  const getMaturityInfo = (score) => {
    if (score >= 3.5) return { label: 'Optimal', color: '#10b981' };
    if (score >= 2.5) return { label: 'Advanced', color: '#3b82f6' };
    if (score >= 1.5) return { label: 'Initial', color: '#eab308' };
    return { label: 'Traditional', color: '#6b7280' };
  };

  const maturityInfo = getMaturityInfo(clampedScore);

  // Gradient colors based on score
  const getGradientColors = (score) => {
    if (score >= 3.5) return { start: '#10b981', end: '#059669' };
    if (score >= 2.5) return { start: '#3b82f6', end: '#2563eb' };
    if (score >= 1.5) return { start: '#eab308', end: '#ca8a04' };
    return { start: '#6b7280', end: '#4b5563' };
  };

  const gradientColors = getGradientColors(clampedScore);
  const gradientId = `gauge-gradient-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className="cisa-maturity-gauge" style={{ width: config.width, height: config.width }}>
      <svg
        width={config.width}
        height={config.width}
        viewBox={`0 0 ${config.width} ${config.width}`}
        className="cisa-maturity-gauge-svg"
      >
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={gradientColors.start} />
            <stop offset="100%" stopColor={gradientColors.end} />
          </linearGradient>
        </defs>

        {/* Background track */}
        <circle
          cx={config.width / 2}
          cy={config.width / 2}
          r={radius}
          fill="none"
          stroke="var(--cisa-surface-3, rgba(255,255,255,0.1))"
          strokeWidth={config.strokeWidth}
          strokeDasharray={`${arcLength} ${circumference}`}
          strokeLinecap="round"
          transform={`rotate(135 ${config.width / 2} ${config.width / 2})`}
        />

        {/* Progress arc */}
        <circle
          cx={config.width / 2}
          cy={config.width / 2}
          r={radius}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth={config.strokeWidth}
          strokeDasharray={`${arcLength} ${circumference}`}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          transform={`rotate(135 ${config.width / 2} ${config.width / 2})`}
          className={animate ? 'cisa-maturity-gauge-progress' : ''}
          style={{
            transition: animate ? 'stroke-dashoffset 0.8s ease-out' : 'none'
          }}
        />

        {/* Score text */}
        <text
          x={config.width / 2}
          y={config.width / 2 - (showLabel ? config.labelSize / 2 : 0)}
          textAnchor="middle"
          dominantBaseline="central"
          fill="var(--cisa-text, #e6e9ef)"
          fontSize={config.fontSize}
          fontWeight="700"
        >
          {clampedScore.toFixed(1)}
        </text>

        {/* Maturity level label */}
        {showLabel && (
          <text
            x={config.width / 2}
            y={config.width / 2 + config.fontSize / 2 + 4}
            textAnchor="middle"
            dominantBaseline="central"
            fill={maturityInfo.color}
            fontSize={config.labelSize}
            fontWeight="600"
          >
            {maturityInfo.label}
          </text>
        )}
      </svg>
    </div>
  );
};

export default MaturityGauge;
